import React, { useState, useEffect } from 'react';
import EditableSection from './EditableSection';
import { Plus, Trash2, GripVertical } from 'lucide-react';

const SkillsEditor = ({ data, onSave, onRemove }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data || []);

  useEffect(() => {
    if (data) setEditData(data);
  }, [data]);

  const handleSave = () => {
    onSave(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data || []);
    setIsEditing(false);
  };

  const addSkill = () => setEditData([...editData, { title: '', description: '' }]);
  
  const updateSkillTitle = (index, value) => {
    const newData = [...editData];
    if (typeof newData[index] === 'string') {
      newData[index] = { title: value, description: newData[index] };
    } else {
      newData[index] = { ...newData[index], title: value };
    }
    setEditData(newData);
  };

  const updateSkillDescription = (index, value) => {
    const newData = [...editData];
    if (typeof newData[index] === 'string') {
      newData[index] = { title: '', description: value };
    } else {
      newData[index] = { ...newData[index], description: value };
    }
    setEditData(newData);
  };
  
  const removeSkill = (index) => {
    const newData = editData.filter((_, i) => i !== index);
    setEditData(newData);
  };

  const renderSkill = (skill) => {
    if (!skill) return null;
    
    if (typeof skill === 'string') {
      const colonIndex = skill.indexOf(':');
      if (colonIndex !== -1) {
        const category = skill.substring(0, colonIndex + 1);
        const values = skill.substring(colonIndex + 1);
        return (
          <>
            <span className="font-bold">{category}</span>
            {values}
          </>
        );
      }
      return skill;
    }

    const desc = Array.isArray(skill.description) ? skill.description.join(', ') : skill.description;
    return (
      <>
        {skill.title && <span className="font-bold">{skill.title}: </span>}
        {desc}
      </>
    );
  };

  return (
    <EditableSection 
      title="Skills"
      isEditing={isEditing} 
      onEdit={() => setIsEditing(true)} 
      onCancel={handleCancel} 
      onSave={handleSave}
      onRemove={onRemove}
    >
      {isEditing ? (
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200 font-sans">
          <label className="block text-sm font-medium text-gray-700 mb-3">Skills List</label>
          <div className="space-y-2 mb-3">
            {editData.map((skill, idx) => {
              const skillTitle = typeof skill === 'string' ? '' : (skill?.title || '');
              const skillDesc = typeof skill === 'string' ? skill : (Array.isArray(skill?.description) ? skill.description.join(', ') : (skill?.description || ''));
              
              return (
              <div key={idx} className="flex gap-2 items-center">
                <GripVertical size={16} className="text-gray-400 cursor-move" />
                <input 
                  type="text" 
                  value={skillTitle} 
                  onChange={(e) => updateSkillTitle(idx, e.target.value)}
                  className="w-1/3 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder="Category (e.g. Backend)"
                />
                <input 
                  type="text" 
                  value={skillDesc} 
                  onChange={(e) => updateSkillDescription(idx, e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder="Skills (e.g. Python, JavaScript)"
                />
                <button onClick={() => removeSkill(idx)} className="text-red-500 hover:text-red-700 p-2 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            )})}
            {editData.length === 0 && <p className="text-sm text-gray-500 italic py-2">No skills added yet.</p>}
          </div>
          <button onClick={addSkill} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium">
            <Plus size={16} /> Add Skill
          </button>
        </div>
      ) : (
        <div className="resume-content text-[15px] leading-relaxed text-gray-900">
          {(!data || data.length === 0) ? (
            <p className="text-gray-400 italic">No skills provided.</p>
          ) : (
            <ul className="space-y-[4px]">
              {data.map((skill, idx) => (
                <li key={idx}>
                  {renderSkill(skill)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </EditableSection>
  );
};

export default SkillsEditor;
